import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ApiHook } from "../../hooks/apiHook";
import UserSearchSelect from "../Common/UserSearchSelect";

const ReportForm = ({ partials }) => {
    const { t }        = useTranslation();
    const navigate     = useNavigate();

    const {
        register,
        setValue,
        handleSubmit,
        reset,
        trigger,
        formState: { errors },
    } = useForm({});

    const [selectedCategory,    setSelectedCategory]    = useState("");
    const [selectedUserDisplay, setSelectedUserDisplay] = useState("");

    const reportMutation = ApiHook.CallCreateReport();

    const categoryOptions = partials?.categories?.map((c) => ({
        label: t(c.label),
        value: c.value,
    })) ?? [];

    // Called when user picks a suggestion from the dropdown
    const handleUserSelect = (user) => {
        setSelectedUserDisplay(user.username);
        setValue("reportedUserId", user.id, { shouldValidate: true });
    };

    // Called when user edits the input after having picked someone —
    // clears the hidden field so stale IDs are never submitted
    const handleUserClear = () => {
        setValue("reportedUserId", "", { shouldValidate: false });
    };

    const handleCategoryChange = (option) => {
        setValue("category", option?.value ?? "", { shouldValidate: true });
        setSelectedCategory(option);
    };

    const onSubmit = (data) => {
        reportMutation.mutate(data, {
            onSuccess: (res) => {
                if (res?.status) {
                    toast.success(t(res.data));
                    reset();
                    setSelectedCategory("");
                    setSelectedUserDisplay("");
                    navigate("/reports");
                } else {
                    if (res?.data?.description) {
                        toast.error(t(res.data.description));
                    }
                }
            },
        });
    };

    return (
        <>
            <div className="page_head_top">{t("create_report")}</div>
            <div className="support_cnt_box">
                <div className="row">
                    <div className="col-md-6">
                        <form onSubmit={handleSubmit(onSubmit)}>

                            {/* Reported User — autocomplete */}
                            <div className="create_ticket_row">
                                <label>
                                    {t("reported_user")} <span className="text-danger">*</span>
                                </label>
                                {/* Hidden field bound to RHF so validation fires correctly */}
                                <input
                                    type="hidden"
                                    {...register("reportedUserId", {
                                        required: t("this_field_is_required"),
                                    })}
                                />
                                <UserSearchSelect
                                    value={selectedUserDisplay}
                                    onSelect={handleUserSelect}
                                    onClear={handleUserClear}
                                    placeholder={t("search_user_placeholder")}
                                    error={errors.reportedUserId?.message}
                                />
                            </div>

                            {/* Category */}
                            <div className="create_ticket_row">
                                <label>
                                    {t("report_category")} <span className="text-danger">*</span>
                                </label>
                                <Select
                                    value={selectedCategory}
                                    {...register("category", {
                                        required: t("this_field_is_required"),
                                    })}
                                    onBlur={async () => await trigger("category")}
                                    onChange={handleCategoryChange}
                                    options={categoryOptions}
                                />
                                {errors.category && (
                                    <span role="alert" className="error-message-validator">
                                        {errors.category.message}
                                    </span>
                                )}
                            </div>

                            {/* Subject */}
                            <div className="create_ticket_row">
                                <label>
                                    {t("subject")} <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    {...register("subject", {
                                        required:  t("this_field_is_required"),
                                        minLength: { value: 3, message: t("this_field_is_required") },
                                    })}
                                />
                                {errors.subject && (
                                    <span role="alert" className="error-message-validator">
                                        {errors.subject.message}
                                    </span>
                                )}
                            </div>

                            {/* Description */}
                            <div className="create_ticket_row">
                                <label>
                                    {t("report_description")} <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="5"
                                    {...register("description", {
                                        required:  t("this_field_is_required"),
                                        minLength: { value: 10, message: t("this_field_is_required") },
                                    })}
                                />
                                {errors.description && (
                                    <span role="alert" className="error-message-validator">
                                        {errors.description.message}
                                    </span>
                                )}
                            </div>

                            {/* Attachments */}
                            <div className="create_ticket_row">
                                <label>{t("attachment")}</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    multiple
                                    {...register("attachment")}
                                />
                            </div>

                            <div className="col-md-12">
                                <button
                                    type="submit"
                                    className="btn btn-primary float-end"
                                    disabled={reportMutation.status === "loading"}
                                >
                                    {t("submit")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReportForm;
